import { ContactType } from '@api/generated/graphql';
import { cleanPhone } from '@lib/utils/cleanPhone';
import { z } from 'zod';
import strings from './strings';

// Takes a string, cleans it and then validates a phone number. e.g. 1234567890
export const isValidPhoneNumber = (value: string): boolean => {
  const cleanedPhone = cleanPhone(value);
  const phoneNumberRegex = /^\d{10,}$/;
  return phoneNumberRegex.test(cleanedPhone);
};

const contactsBaseValidationSchema = z.object({
  id: z.optional(z.union([z.string(), z.number()])),
  memo: z.string(),
  jobRemindersEnabled: z.boolean(),
  invoiceStatementNotification: z.boolean(),
  marketingUpdatesEnabled: z.boolean(),
});

const phoneValueValidationSchema = z.object({
  type: z.enum([ContactType.Phone, ContactType.MobilePhone, ContactType.Fax]),
  value: z
    .string()
    .refine(
      (number) => isValidPhoneNumber(number),
      strings.errors[ContactType.Phone],
    ),
});

// if the contact method type is 'email', validate the email field
const emailValueValidationSchema = z.object({
  type: z.literal(ContactType.Email),
  value: z.string().email(strings.errors[ContactType.Email]),
});

// this is here to satisfy ContactType.Unknown typing, we should never need this.
const unknownValueValidationSchema = z.object({
  type: z.literal(ContactType.Unknown),
  value: z.string(),
});

// validates the contactMethods array field, will validate the email or number field based on the 'type' field
const phoneOrEmailValidationSchema = z.discriminatedUnion('type', [
  phoneValueValidationSchema,
  emailValueValidationSchema,
  unknownValueValidationSchema,
]);

// combines the customer and billing validation schemas into one schema
export const contactMethodsFormValidationSchema = z.array(
  z.intersection(contactsBaseValidationSchema, phoneOrEmailValidationSchema),
);

export type ContactFormValidationType = z.TypeOf<
  typeof contactMethodsFormValidationSchema
>;

export default contactMethodsFormValidationSchema;
